/* Wolden — live collection product count.
 *
 * Background:
 *   sections/main-collection.liquid + search-results.liquid skip products
 *   matching `metafields.custom.original_vs_pet_swap == "Custom"` at render
 *   time. The displayed count was using `collection.products_count` (raw),
 *   so the visible number was wildly higher than the rendered grid. A
 *   hardcoded `cached_custom_count = 4004` subtraction was applied as a
 *   stop-gap; this script replaces that with a live count.
 *
 * How it works:
 *   1. On collection pages, walk `?view=count-only&page=N` (alternate
 *      Liquid template at templates/collection.count-only.liquid) which
 *      returns a tiny JSON payload per page.
 *   2. Sum `page_visible` across all pages.
 *   3. Patch the count text in the DOM (there are typically two count
 *      <span title="…content.product_count…"> nodes — desktop right
 *      controls and mobile/horizontal toggle).
 *
 * Caching strategy:
 *   - We do NOT cache-bust the per-page fetch URL. Shopify's CDN caches
 *     each `?view=count-only&page=N` response (TTL ~10min default), so
 *     hot pages are cheap. The count will be slightly stale at most by
 *     ~10min, which is fine — the orchestrator creates Customs at
 *     ~40-100/day so the count drifts by a handful at most.
 *   - We additionally cache the final tally in sessionStorage keyed by
 *     `(pathname, day)`, so a user browsing several collection pages in
 *     one session only walks the endpoint once per (collection, day).
 *
 * Caveats:
 *   - Liquid only exposes one page of collection.products at a time, even
 *     paginated by 250. For /collections/all (~4,800 raw) that's ~20
 *     requests. We fetch sequentially the first time; subsequent same-
 *     session collection visits are free.
 *   - If JS fails or the endpoint 404s, the hardcoded Liquid fallback
 *     (cached_custom_count = 4004 in sections/main-collection.liquid)
 *     keeps a sane number on screen.
 */

(function () {
  'use strict';

  var STORAGE_PREFIX = 'wolden_count_v1__';
  var MAX_PAGES = 50; // safety cap: ~12,500 raw products at 250/page.

  // Bail unless we're on a collection page. Shopify exposes the template
  // name on the <main data-template> attribute set in layout/theme.liquid.
  function isCollectionPage() {
    var main = document.querySelector('main[data-template]');
    if (!main) return false;
    var tpl = (main.getAttribute('data-template') || '').toLowerCase();
    // Matches "collection" exactly + any alt-template suffix ("collection.foo").
    return tpl === 'collection' || tpl.indexOf('collection.') === 0;
  }

  if (!isCollectionPage()) return;

  // Per-day cache key. The orchestrator creates ~40-100 Customs/day during
  // active periods, so a daily refresh keeps the visible count within
  // ~5% of truth without re-walking 20 endpoints on every page load.
  function cacheKey(pathname) {
    var d = new Date();
    var day = d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate();
    return STORAGE_PREFIX + pathname + '@' + day;
  }

  function readCache(pathname) {
    try {
      var raw = window.sessionStorage.getItem(cacheKey(pathname));
      if (!raw) return null;
      var n = parseInt(raw, 10);
      return isFinite(n) && n >= 0 ? n : null;
    } catch (_) { return null; }
  }

  function writeCache(pathname, n) {
    try { window.sessionStorage.setItem(cacheKey(pathname), String(n)); } catch (_) {}
  }

  // The count is rendered inside <span title="…">N products</span>. The title
  // attribute uses the same translation key in both desktop + mobile blocks
  // (see blocks/filters.liquid lines 197-202 and 279-282), so we target by
  // the surrounding wrapper class to be specific. Fall back to title-attr
  // selector if the wrapper class changes upstream.
  function findCountSpans() {
    var spans = [];
    document.querySelectorAll('.products-count-wrapper span[title], .gen\\:ml-auto > span[title]').forEach(function (el) {
      var txt = (el.textContent || '').trim();
      // Sanity check: the existing text should look like "1234 products" or
      // "1 product". Skip anything that doesn't, to avoid hijacking some
      // unrelated <span title>.
      if (/^\d[\d,\s]*\s+products?$/i.test(txt)) spans.push(el);
    });
    return spans;
  }

  function updateSpans(visibleCount) {
    var spans = findCountSpans();
    if (!spans.length) return;
    var label = visibleCount === 1 ? 'product' : 'products';
    var formatted = visibleCount.toLocaleString();
    spans.forEach(function (el) {
      // Preserve the leading whitespace inside the original Liquid output
      // (`{{- products_count }} ...`). The minus-trim makes it start with
      // no whitespace, so we just replace the entire text.
      el.textContent = formatted + ' ' + label;
    });
  }

  function fetchPageCount(pathname, page) {
    // No cache-buster — let Shopify's CDN cache each page's response.
    // Stale by up to a few minutes is fine (Customs are created at
    // ~40-100/day; the count drifts by single digits over that window).
    //
    // Important: do NOT send `Accept: application/json`. Shopify routes
    // requests with that header to its JSON product API path, which
    // doesn't honour `?view=` alternate templates and 404s. Default
    // Accept (browser-supplied `text/html, ...`) is what makes the
    // alternate Liquid template fire.
    var url = pathname + '?view=count-only&page=' + page;
    return fetch(url, {
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('count-only HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      // Trim — Liquid's whitespace control can leave leading/trailing
      // newlines around the JSON payload.
      var trimmed = text.trim();
      // Defensive: if Shopify served the full theme layout instead of our
      // alternate template (template not deployed yet, or `?view=` ignored),
      // this won't be valid JSON. Surface that as an error so the catch
      // below leaves the Liquid-rendered count alone.
      if (trimmed.charAt(0) !== '{') {
        throw new Error('count-only returned non-JSON (template not deployed?)');
      }
      return JSON.parse(trimmed);
    });
  }

  function tallyCollection(pathname) {
    var totalVisible = 0;
    var page = 1;

    function next() {
      return fetchPageCount(pathname, page).then(function (data) {
        totalVisible += (data.page_visible || 0);
        if (data.has_more && page < MAX_PAGES) {
          page += 1;
          return next();
        }
        return totalVisible;
      });
    }

    return next();
  }

  function run() {
    var pathname = window.location.pathname;
    if (!/^\/collections\//.test(pathname)) return;

    // Some Shopify themes duplicate the collection URL with a trailing slash;
    // normalise to one with no trailing slash for the count fetch and cache.
    pathname = pathname.replace(/\/+$/, '');

    // Hit cache before walking the endpoint.
    var cached = readCache(pathname);
    if (cached !== null) {
      updateSpans(cached);
      return;
    }

    tallyCollection(pathname).then(function (count) {
      writeCache(pathname, count);
      updateSpans(count);
    }).catch(function (err) {
      // Keep this quiet in production but useful for the one debugging session.
      // The Liquid-rendered fallback count remains visible on failure.
      if (window.console && window.console.warn) {
        window.console.warn('[wolden-collection-count] live count failed, leaving Liquid fallback:', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
